import { useCallback, useEffect, useRef, useState } from 'react';
import { getCableConsumer } from '../lib/cableConsumer';

/**
 * Subscribe to LivePresenceChannel for a given session.
 *
 * Pass a `handlers` object whose keys map to event types broadcast by the
 * channel. Each handler receives the raw payload object.
 *
 *   useLivePresenceChannel(sessionId, {
 *     onSessionState: ({ status, current_view, current_view_params }) => {...},
 *     onSetView:      ({ view, params }) => {...},
 *     onSessionStatus:({ status, started_at, completed_at }) => {...},
 *     onCursor:       ({ user_id, role, x, y }) => {...},
 *     onHighlight:    ({ selector, start, end, text }) => {...},
 *     onScrollSync:   ({ top_pct }) => {...},
 *     onInput:        ({ user_id, field_id, value }) => {...},
 *     onStep:         ({ direction }) => {...},
 *   });
 *
 * Returns `{ perform }` so the caller can broadcast back, e.g.
 * `perform('set_view', { view, params })` or `perform('step', { direction })`.
 *
 * Handlers are kept fresh via a ref so the subscription itself only re-runs
 * when sessionId changes — re-rendering the consumer doesn't tear down the
 * cable connection.
 */
export function useLivePresenceChannel(sessionId, handlers) {
  const handlersRef = useRef(handlers);
  const subRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    if (!sessionId) return;

    const cable = getCableConsumer();
    subRef.current = cable.subscriptions.create(
      { channel: 'LivePresenceChannel', session_id: sessionId },
      {
        connected() {
          setIsConnected(true);
        },
        disconnected() {
          setIsConnected(false);
        },
        rejected() {
          setIsConnected(false);
        },
        received(data) {
          const h = handlersRef.current || {};
          switch (data?.type) {
            case 'session_state':
              h.onSessionState?.(data); break;
            case 'set_view':
              h.onSetView?.(data); break;
            case 'session_status':
              h.onSessionStatus?.(data); break;
            case 'cursor_move':
              h.onCursor?.(data); break;
            case 'highlight':
              h.onHighlight?.(data); break;
            case 'scroll_sync':
              h.onScrollSync?.(data); break;
            case 'reader_pin':
              h.onReaderPin?.(data); break;
            case 'reader_lock':
              h.onReaderLock?.(data); break;
            case 'set_chapter':
              h.onSetChapter?.(data); break;
            case 'reader_selections':
              h.onReaderSelections?.(data); break;
            case 'input_update':
              h.onInput?.(data); break;
            case 'step':
              h.onStep?.(data); break;
            case 'progress_state':
              h.onProgress?.(data); break;
            case 'focus_target':
              h.onFocus?.(data); break;
            case 'away_state':
              h.onAway?.(data); break;
            case 'presence_update':
              h.onPresence?.(data); break;
            case 'peer_intent':
              h.onPeerIntent?.(data); break;
            case 'activity_ready':
              h.onActivityReady?.(data); break;
            case 'error':
              h.onError?.(data); break;
            default:
              h.onUnknown?.(data);
          }
        },
      }
    );

    return () => {
      if (subRef.current) {
        subRef.current.unsubscribe();
        subRef.current = null;
      }
      setIsConnected(false);
    };
  }, [sessionId]);

  // Stable across renders so consumers (useHighlightSync, useInputBroadcast,
  // LiveCursorOverlay) don't re-attach their listeners every time the
  // parent re-renders — which would clobber any in-flight debounce timers.
  const perform = useCallback((action, payload) => {
    subRef.current?.perform(action, payload || {});
  }, []);

  return { perform, isConnected };
}
